import { useEffect, useState } from 'react';
import type { ChangeEvent, FC, FormEvent, InputHTMLAttributes } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ParticlesWithLightRays } from '../ParticlesWithLightRays';
import { ReactComponent as SailTrackLogo } from '../react-icons/SailTrackLogo.svg';
import { fetchAppConfig } from '../services/config';
import { setPlanPromptReason } from '../services/subscription';
import '../styles/AuthUser.css';
import { setAuthToken } from '../utils/auth';

interface LoginResponse {
  success: boolean;
  token: string;
  userId: number;
}

interface RegisterResponse {
  success: boolean;
  message: string;
  token?: string;
  userId?: number;
  verificationSkipped?: boolean;
  phoneVerificationCode?: number | string;
  emailVerificationCode?: number | string;
}

type AuthFieldName =
  | 'identifier'
  | 'password'
  | 'fullName'
  | 'phoneNumber'
  | 'email'
  | 'confirmEmail'
  | 'confirmPassword';

interface AuthTextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  id: string;
  label: string;
  hint?: string;
  invalid?: boolean;
}

const AuthTextField: FC<AuthTextFieldProps> = ({
  id,
  label,
  hint,
  invalid = false,
  className = '',
  ...inputProps
}) => {
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div className={`auth-field ${invalid ? 'is-invalid' : ''}`}>
      <label htmlFor={id}>{label}</label>
      <input
        {...inputProps}
        id={id}
        className={`auth-input ${className}`}
        aria-invalid={invalid ? 'true' : undefined}
        aria-describedby={hintId}
      />
      {hint ? (
        <p id={hintId} className="auth-field-hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL ?? '';

export const AuthUser: FC = () => {
  const { t } = useTranslation('common');
  const AUTH_INVALID_CREDENTIALS_KEY = 'auth.invalidCredentials';
  const AUTH_GENERIC_ERROR_KEY = 'auth.genericError';
  const AUTH_REGISTER = 'auth.register';
  const AUTH_LOGIN = 'auth.login';
  const [isRegistering, setIsRegistering] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone_number, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [invalidFields, setInvalidFields] = useState<AuthFieldName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/home');
    } else {
      setIsLoading(false);
    }
  }, [navigate]);

  const markInvalid = (fields: AuthFieldName[], message: string) => {
    setInvalidFields(fields);
    setError(message);
  };

  const isFieldInvalid = (field: AuthFieldName) => invalidFields.includes(field);

  const handleInputChange =
    (
      field: AuthFieldName,
      setter: (value: string) => void,
      normalize: (value: string) => string = (value) => value,
    ) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(normalize(event.target.value));
      setInvalidFields((previous) => previous.filter((current) => current !== field));
    };

  const handleModeChange = (nextIsRegistering: boolean) => {
    setIsRegistering(nextIsRegistering);
    setError('');
    setInvalidFields([]);
  };

  const handleLogin = async (error_: FormEvent) => {
    error_.preventDefault();
    setError('');
    setInvalidFields([]);
    if (!identifier.trim() || !password) {
      markInvalid(
        ['identifier', 'password'],
        t('auth.requiredFields', 'Please enter your email or phone and password.'),
      );
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post<LoginResponse>(`${API_BASE_URL}/api/auth/login`, {
        identifier: identifier.trim().toLowerCase(),
        password,
      });
      if (response.data.success) {
        setAuthToken(response.data.token);
        await fetchAppConfig();
        navigate('/home');
      } else {
        markInvalid(['identifier', 'password'], t(AUTH_INVALID_CREDENTIALS_KEY));
      }
    } catch (error_) {
      if (axios.isAxiosError(error_) && error_.response?.status === 401) {
        markInvalid(['identifier', 'password'], t(AUTH_INVALID_CREDENTIALS_KEY));
        return;
      }
      console.error('Error logging in:', error_);
      setError(t(AUTH_GENERIC_ERROR_KEY));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (error_: FormEvent) => {
    error_.preventDefault();
    setError('');
    setInvalidFields([]);

    const missingFields: AuthFieldName[] = [];
    if (!fullName.trim()) missingFields.push('fullName');
    if (!phone_number.trim()) missingFields.push('phoneNumber');
    if (!email.trim()) missingFields.push('email');
    if (!confirmEmail.trim()) missingFields.push('confirmEmail');
    if (!password) missingFields.push('password');
    if (!confirmPassword) missingFields.push('confirmPassword');

    if (missingFields.length > 0) {
      markInvalid(
        missingFields,
        t('auth.requiredRegisterFields', 'Please complete all account fields.'),
      );
      return;
    }
    if (email.trim() !== confirmEmail.trim()) {
      markInvalid(['email', 'confirmEmail'], t('auth.emailsNoMatch'));
      return;
    }
    if (password !== confirmPassword) {
      markInvalid(['password', 'confirmPassword'], t('auth.passwordsNoMatch'));
      return;
    }

    const registrationData = {
      fullName: fullName.trim(),
      phone_number: phone_number.trim(),
      email: email.trim().toLowerCase(),
      password,
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post<RegisterResponse>(
        `${API_BASE_URL}/api/auth/register`,
        registrationData,
      );

      if (response.data.success) {
        if (response.data.verificationSkipped && response.data.token) {
          setAuthToken(response.data.token);
          setPlanPromptReason('new-user');
          await fetchAppConfig();
          navigate('/home');
          return;
        }

        const registrationState = {
          ...registrationData,
          ...(response.data.phoneVerificationCode
            ? { phoneVerificationCode: String(response.data.phoneVerificationCode) }
            : {}),
          ...(response.data.emailVerificationCode
            ? { emailVerificationCode: String(response.data.emailVerificationCode) }
            : {}),
        };
        setPlanPromptReason('new-user');
        await fetchAppConfig();
        navigate('/phone-verification', { state: { registrationData: registrationState } });
      } else {
        setError(response.data.message || t('admin.userCreationFailed'));
      }
    } catch (error_) {
      if (axios.isAxiosError(error_) && error_.response?.data?.message) {
        setError(error_.response.data.message);
        return;
      }
      console.error('Error registering:', error_);
      setError(t(AUTH_GENERIC_ERROR_KEY));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <main className="auth-loading" role="status" aria-live="polite">
        {t('auth.loading')}
      </main>
    );
  }

  return (
    <main className={`auth-container ${isRegistering ? 'register-mode' : 'login-mode'}`}>
      <ParticlesWithLightRays />
      <section className="auth-shell" aria-labelledby="auth-page-title">
        <aside className="auth-overview" aria-label={t('auth.overviewLabel', 'SailTrack access')}>
          <div>
            <div className="auth-brand-lockup" aria-label="SailTrack">
              <SailTrackLogo className="auth-brand-mark" aria-hidden="true" focusable="false" />
              <span className="auth-brand-name">SailTrack</span>
            </div>
            <p className="auth-context-label">
              {t('auth.workspaceLabel', 'Race performance workspace')}
            </p>
            <h1 id="auth-page-title">
              {isRegistering
                ? t('auth.registerTitle', 'Create your SailTrack account')
                : t('auth.loginTitle', 'Access your race workspace')}
            </h1>
            <p className="auth-overview-copy">
              {isRegistering
                ? t(
                    'auth.registerSubtitle',
                    'Set up secure access before adding boats, sessions, and reports.',
                  )
                : t(
                    'auth.loginSubtitle',
                    'Continue to tracks, polars, analytics, and reports without changing context.',
                  )}
            </p>
          </div>
          <ul className="auth-context-list">
            <li>
              {t('auth.contextTracks', 'Track analysis, reports, and boat settings stay together.')}
            </li>
            <li>
              {t('auth.contextSecurity', 'Use the team account before uploading new session data.')}
            </li>
          </ul>
        </aside>

        <div className="auth-form-panel">
          <div
            className="auth-mode-switch"
            role="group"
            aria-label={t('auth.modeLabel', 'Authentication mode')}
          >
            <button
              type="button"
              className="auth-mode-button"
              aria-pressed={!isRegistering}
              onClick={() => handleModeChange(false)}
            >
              {t(AUTH_LOGIN)}
            </button>
            <button
              type="button"
              className="auth-mode-button"
              aria-pressed={isRegistering}
              onClick={() => handleModeChange(true)}
            >
              {t(AUTH_REGISTER)}
            </button>
          </div>

          {isRegistering ? (
            <form
              className="auth-form"
              onSubmit={handleRegister}
              aria-labelledby="auth-form-title"
              aria-describedby={error ? 'auth-error' : undefined}
              noValidate
            >
              <div className="auth-form-heading">
                <h2 id="auth-form-title">{t(AUTH_REGISTER)}</h2>
                <p>{t('auth.registerHint', 'Use the contact details your crew will recognize.')}</p>
              </div>
              <div className="auth-trial-note" aria-label={t('auth.trialAriaLabel')}>
                <strong>{t('auth.trialPromise')}</strong>
                <span>{t('auth.trialNoCommitment')}</span>
              </div>
              <AuthTextField
                id="auth-full-name"
                label={t('auth.fullName')}
                type="text"
                value={fullName}
                onChange={handleInputChange('fullName', setFullName)}
                autoComplete="name"
                required
                invalid={isFieldInvalid('fullName')}
              />
              <AuthTextField
                id="auth-phone"
                label={t('auth.phoneNumber')}
                type="tel"
                value={phone_number}
                onChange={handleInputChange('phoneNumber', setPhoneNumber)}
                autoComplete="tel"
                inputMode="tel"
                required
                invalid={isFieldInvalid('phoneNumber')}
              />
              <AuthTextField
                id="auth-email"
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={handleInputChange('email', setEmail, (value) => value.toLowerCase())}
                autoComplete="email"
                inputMode="email"
                required
                invalid={isFieldInvalid('email')}
              />
              <AuthTextField
                id="auth-confirm-email"
                label={t('auth.confirmEmail')}
                type="email"
                value={confirmEmail}
                onChange={handleInputChange('confirmEmail', setConfirmEmail, (value) =>
                  value.toLowerCase(),
                )}
                autoComplete="email"
                inputMode="email"
                required
                invalid={isFieldInvalid('confirmEmail')}
              />
              <AuthTextField
                id="auth-register-password"
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={handleInputChange('password', setPassword)}
                autoComplete="new-password"
                required
                invalid={isFieldInvalid('password')}
                hint={t(
                  'auth.passwordRequirement',
                  'Use at least 8 characters with a mix of letters and numbers.',
                )}
              />
              <AuthTextField
                id="auth-confirm-password"
                label={t('auth.confirmPassword')}
                type="password"
                value={confirmPassword}
                onChange={handleInputChange('confirmPassword', setConfirmPassword)}
                autoComplete="new-password"
                required
                invalid={isFieldInvalid('confirmPassword')}
              />
              <button className="auth-primary-action" type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t('auth.creatingAccount', 'Creating account...')
                  : t('auth.createAccountAction', 'Start 14 days free')}
              </button>
            </form>
          ) : (
            <form
              className="auth-form"
              onSubmit={handleLogin}
              aria-labelledby="auth-form-title"
              aria-describedby={error ? 'auth-error' : undefined}
              noValidate
            >
              <div className="auth-form-heading">
                <h2 id="auth-form-title">{t(AUTH_LOGIN)}</h2>
                <p>{t('auth.loginHint', 'Use your account email or verified phone number.')}</p>
              </div>
              <AuthTextField
                id="auth-identifier"
                label={t('auth.emailOrPhone')}
                type="text"
                value={identifier}
                onChange={handleInputChange('identifier', setIdentifier, (value) =>
                  value.toLowerCase(),
                )}
                autoComplete="username"
                inputMode="email"
                required
                invalid={isFieldInvalid('identifier')}
              />
              <AuthTextField
                id="auth-password"
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={handleInputChange('password', setPassword)}
                autoComplete="current-password"
                required
                invalid={isFieldInvalid('password')}
              />
              <button className="auth-primary-action" type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t('auth.signingIn', 'Signing in...')
                  : t('auth.signInAction', 'Sign in')}
              </button>
            </form>
          )}

          {error ? (
            <p id="auth-error" className="error-authuser" role="alert">
              {error}
            </p>
          ) : null}

          <p className={`auth-mode-support${isRegistering ? '' : ' auth-mode-support--trial'}`}>
            {isRegistering ? t('auth.alreadyHaveAccount') : t('auth.noAccountTrial')}{' '}
            <button
              type="button"
              className={isRegistering ? undefined : 'auth-secondary-trial-action'}
              onClick={() => handleModeChange(!isRegistering)}
            >
              {isRegistering ? t('auth.goToLogin') : t('auth.goToRegisterTrial')}
            </button>
          </p>
        </div>
      </section>
    </main>
  );
};
